// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Types
import type { SearchResult } from './megaMenuOverlay.types';

const IMAGE_SIZE = 200;
const CARD_HEIGHT_MIN = 200;
const CARD_HEIGHT_MAX = 400;
const CARD_FLUID_HEIGHT = `clamp(${CARD_HEIGHT_MIN}px, calc(${CARD_HEIGHT_MAX}px - (100vw - 360px) * ${(CARD_HEIGHT_MAX - CARD_HEIGHT_MIN) / 540}), ${CARD_HEIGHT_MAX}px)`;

export type SearchResultCardProps = {
	result: SearchResult;
};

const SearchResultCard = ({ result }: SearchResultCardProps) => {
	const theme = useTheme();
	const { category, date, excerpt, typeLabel, imageSrc, title, href } = result;
	const rootComponent = href ? 'a' : 'article';

	return (
		<Box component="li" sx={{ listStyle: 'none' }}>
			<Box
				component={rootComponent}
				{...(href ? { href, 'aria-label': title } : {})}
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					width: '100%',
					height: CARD_FLUID_HEIGHT,
					maxHeight: { sm: CARD_HEIGHT_MIN },
					minHeight: 0,
					boxSizing: 'border-box',
					textAlign: 'left',
					textDecoration: 'none',
					color: 'inherit',
					backgroundColor: 'common.white',
					borderRadius: {
						xs: 2,
						sm: 1
					},
					position: 'relative',
					overflow: 'hidden',
					border: 'none',
					padding: 0,
					cursor: href ? 'pointer' : 'default',
					// Hover/focus ring via ::after — avoids outline glitches with sticky filters and root border/box-shadow issues.
					'&::after': {
						content: '""',
						position: 'absolute',
						inset: 0,
						borderRadius: 'inherit',
						pointerEvents: 'none',
						zIndex: 1,
						opacity: 0,
						border: `1.5px solid ${theme.palette.grey[300]}`,
						transition: theme.transitions.create(['opacity', 'border-color'], {
							duration: theme.transitions.duration.shorter
						})
					},
					'&:hover::after': {
						opacity: 1
					},
					'&:focusVisible': {
						outline: 'none'
					},
					'&:focusVisible::after': {
						opacity: 1,
						borderColor: theme.palette.primary.main
					},
					'& .mega-search-arrow': {
						display: 'inline-block',
						transition: 'transform 0.2s ease'
					},
					'&:hover .mega-search-arrow': {
						transform: 'translateX(4px)'
					}
				}}
			>
				<Box
					sx={{
						position: 'relative',
						flex: {
							xs: '0 0 45%',
							sm: `0 0 ${IMAGE_SIZE}px`
						},
						minHeight: { xs: 0 },
						maxHeight: { xs: 'none' },
						width: { sm: IMAGE_SIZE },
						height: { sm: IMAGE_SIZE },
						alignSelf: { sm: 'flex-start' },
						flexShrink: { sm: 0 }
					}}
				>
					<Box
						component="img"
						src={imageSrc}
						alt=""
						loading="lazy"
						decoding="async"
						sx={{
							display: 'block',
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							objectPosition: 'center'
						}}
					/>
				</Box>

				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column',
						justifyContent: { xs: 'space-between', sm: 'flex-start' },
						px: { xs: 4, sm: 6 },
						py: { xs: 4, sm: 6 },
						gap: { xs: 4, sm: 6 }
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'baseline',
							justifyContent: 'space-between',
							gap: 2
						}}
					>
						<Typography component="span" variant="body-md" sx={{ color: 'text.primary', minWidth: 0 }}>
							{category}
						</Typography>
						<Typography component="span" variant="body-md" sx={{ color: 'text.tertiary', flexShrink: 0 }}>
							{date}
						</Typography>
					</Box>

					{/* flex: 0 0 auto — excerpt must not grow (would clip before line-clamp …); column space-between still pins CTA. */}
					<Box
						component="p"
						sx={{
							...theme.typography['body-md'],
							flex: '0 0 auto',
							m: 0,
							minWidth: 0,
							maxWidth: { sm: 'calc(100% - 100px)' },
							color: 'text.secondary',
							overflow: 'hidden',
							display: '-webkit-box',
							WebkitBoxOrient: 'vertical',
							WebkitLineClamp: { xs: 3, sm: 2 },
							wordBreak: 'break-word'
						}}
					>
						{excerpt}
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 2,
							mt: { xs: 'auto', sm: 0 },
							pt: { xs: 0.5, sm: 0 }
						}}
					>
						<Typography
							component="span"
							variant="body-md"
							sx={{
								fontWeight: 500,
								color: 'text.secondary',
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.75
							}}
						>
							<span aria-hidden className="mega-search-arrow">
								→
							</span>
							Mehr erfahren
						</Typography>
						<Typography component="span" variant="body-md" sx={{ color: 'text.tertiary', flexShrink: 0 }}>
							{typeLabel}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default SearchResultCard;
